/**
 * Learn — design/PROTOTYPE-SPEC.md §15: "searchable how-to guides grouped by
 * area with per-step 'Show me how' screen tours, FAQs".
 *
 * ## It is also where the tour lives now
 *
 * Until this page existed, the quick tour was reachable exactly once — on the
 * last screen of guided setup. Anyone who skipped it, or who joined a clinic
 * later, had no way to find it. A tour you cannot replay is barely worth
 * building.
 *
 * ## "Show me how" is the tour engine, not a second implementation
 *
 * A guide's steps are turned into `TourStep[]` and handed to the same overlay
 * the quick tour uses. That is the whole reason the engine takes its steps as a
 * prop rather than importing them: one measuring implementation, one set of
 * timing workarounds, one place where the spotlight can be wrong.
 *
 * Search covers titles, step text, areas and FAQ answers together, because
 * someone looking for help does not know whether the answer is a guide or an
 * FAQ — that is our filing system, not theirs.
 */
import { canAccess, type Plan as CorePlan } from '@inventory-tracker/core';
import { useMemo, useState } from 'react';
import { FAQS, GUIDES, type Guide } from './learn/content.js';
import type { TourStep } from './tour/steps.js';
import type { Me } from '../lib/api.js';
import { c, mono, primaryButton, sans } from '../theme.js';
import { cardStyle, smallButton } from './format.js';

interface Props {
  me: Me;
  /** Starts the quick tour. */
  onStartTour: () => void;
  /** Starts a guide as a tour. */
  onStartGuide: (label: string, steps: TourStep[]) => void;
}

/** A guide becomes a tour: one step per line, each with its own anchor. */
export function guideToSteps(guide: Guide): TourStep[] {
  return guide.steps.map((body, i) => ({
    page: guide.page,
    anchor: guide.anchors[i] ?? guide.anchors[guide.anchors.length - 1] ?? 'kpis',
    title: guide.title,
    body,
  }));
}

export function LearnPage({ me, onStartTour, onStartGuide }: Props) {
  const [query, setQuery] = useState('');
  const [openGuide, setOpenGuide] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const plan = me.subscription.plan as CorePlan;

  const guides = useMemo(() => {
    // Gated guides are hidden rather than shown locked: a guide you cannot
    // follow is a dead end, and the upgrade prompt belongs on the feature.
    const available = GUIDES.filter((g) => !g.requires || canAccess(plan, g.requires));
    const q = query.trim().toLowerCase();
    if (!q) return available;
    return available.filter((g) =>
      `${g.area} ${g.title} ${g.steps.join(' ')}`.toLowerCase().includes(q),
    );
  }, [plan, query]);

  const faqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.filter((f) => `${f.q} ${f.a}`.toLowerCase().includes(q));
  }, [query]);

  const nothing = guides.length === 0 && faqs.length === 0;

  return (
    <div data-tour="learn">
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ maxWidth: 460 }}>
            <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 6px' }}>
              Take the tour
            </h2>
            <p style={{ fontSize: 13.5, color: c.muted, margin: 0, lineHeight: 1.7 }}>
              Two minutes, walking through the screens you will use most. You can
              stop at any point.
            </p>
          </div>
          <button
            type="button"
            onClick={onStartTour}
            style={{ ...primaryButton, width: 'auto', height: 44, padding: '0 22px' }}
          >
            Start the tour
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label htmlFor="learnSearch" style={srOnly}>
          Search guides and questions
        </label>
        <input
          id="learnSearch"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search guides and questions. Try “expiry”, “import”, “audit”"
          style={{
            width: '100%',
            height: 46,
            padding: '0 15px',
            borderRadius: 12,
            border: `1px solid ${c.borderStrong}`,
            background: c.white,
            fontSize: 14,
            fontFamily: sans,
            boxSizing: 'border-box',
          }}
        />
      </div>

      {nothing && (
        <div style={{ ...cardStyle, textAlign: 'center', color: c.muted2, fontSize: 13.5 }}>
          Nothing matches “{query}”. Try a shorter word.
        </div>
      )}

      {guides.length > 0 && (
        <Section title="How to">
          {groupByArea(guides).map(([area, list]) => (
            <div key={area} style={{ marginBottom: 10 }}>
              <div style={areaHeading}>{area}</div>
              {list.map((g) => (
                <Row
                  key={g.id}
                  open={openGuide === g.id}
                  onToggle={() => setOpenGuide(openGuide === g.id ? null : g.id)}
                  title={g.title}
                  meta={g.mins}
                >
                  <ol
                    style={{
                      margin: '0 0 14px',
                      paddingLeft: 18,
                      fontSize: 13.5,
                      color: c.muted,
                      lineHeight: 1.8,
                    }}
                  >
                    {g.steps.map((s, i) => (
                      <li key={i} style={{ marginBottom: 6 }}>
                        {s}
                      </li>
                    ))}
                  </ol>
                  <button
                    type="button"
                    onClick={() => onStartGuide(g.title, guideToSteps(g))}
                    style={{ ...smallButton, height: 38, padding: '0 16px' }}
                  >
                    Show me how
                  </button>
                </Row>
              ))}
            </div>
          ))}
        </Section>
      )}

      {faqs.length > 0 && (
        <Section title="Common questions">
          {faqs.map((f) => (
            <Row
              key={f.id}
              open={openFaq === f.id}
              onToggle={() => setOpenFaq(openFaq === f.id ? null : f.id)}
              title={f.q}
            >
              <p style={{ fontSize: 13.5, color: c.muted, lineHeight: 1.8, margin: 0 }}>{f.a}</p>
            </Row>
          ))}
        </Section>
      )}
    </div>
  );
}

/** Preserves the order guides are declared in, which is deliberate. */
function groupByArea(guides: Guide[]): [string, Guide[]][] {
  const out: [string, Guide[]][] = [];
  for (const g of guides) {
    const last = out[out.length - 1];
    if (last && last[0] === g.area) last[1].push(g);
    else out.push([g.area, [g]]);
  }
  return out;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 14px' }}>
        {title}
      </h2>
      {children}
    </div>
  );
}

function Row({
  open,
  onToggle,
  title,
  meta,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  meta?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ borderTop: `1px solid ${c.border}` }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: 'none',
          border: 'none',
          padding: '13px 2px',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: sans,
        }}
      >
        <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: c.ink }}>{title}</span>
        {meta && <span style={{ fontSize: 12, color: c.faint }}>{meta}</span>}
        <span style={{ fontSize: 16, color: c.muted2, width: 14, textAlign: 'center' }}>
          {open ? '−' : '+'}
        </span>
      </button>
      {open && <div style={{ padding: '0 2px 16px' }}>{children}</div>}
    </div>
  );
}

const areaHeading: React.CSSProperties = {
  fontSize: 10.5,
  textTransform: 'lowercase', fontFamily: mono,
  color: c.faint,
  fontWeight: 400,
  padding: '14px 2px 2px',
};

const srOnly: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};
